import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import * as XLSX from 'xlsx';

export const CargaMasivaButton = ({ 
  endpoint, 
  onSuccess, 
  templateColumns,
  templateName,
  entityName 
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Por favor seleccione un archivo Excel (.xlsx o .xls)');
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Seleccione un archivo primero');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      
      if (response.data.created > 0) {
        toast.success(`${response.data.created} ${entityName} creados exitosamente`);
        onSuccess && onSuccess();
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} errores encontrados`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el archivo');
      setResult({ created: 0, errors: [error.response?.data?.detail || 'Error desconocido'] });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Crear libro de Excel
    const wb = XLSX.utils.book_new();
    
    // Crear datos con encabezados y una fila de ejemplo
    const exampleData = templateColumns.map(col => {
      // Generar datos de ejemplo según el tipo de columna
      const examples = {
        'nombre': 'Juan Pérez',
        'rut': '12.345.678-9',
        'telefono': '+56 9 1234 5678',
        'teléfono': '+56 9 1234 5678',
        'email': 'ejemplo@correo.com',
        'correo': 'ejemplo@correo.com',
        'direccion': 'Av. Principal 123',
        'dirección': 'Av. Principal 123',
        'centros_costo': 'Ventas, Operaciones, Administración',
        'patente': 'ABCD12',
        'marca': 'Toyota',
        'modelo': 'Hiace',
        'año': 2024,
        'capacidad': 12,
        'origen': 'Santiago Centro',
        'destino': 'Aeropuerto',
        'tarifa': 25000,
        'precio': 25000,
        'password': '1234',
        'contraseña': '1234',
        'role': 'client',
        'rol': 'client',
        'empresa': 'Nombre de Empresa'
      };
      return examples[col.toLowerCase()] || `ejemplo_${col}`;
    });

    const wsData = [
      templateColumns,  // Encabezados
      exampleData       // Fila de ejemplo
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Ajustar ancho de columnas
    const colWidths = templateColumns.map(col => ({ wch: Math.max(col.length + 5, 15) }));
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Datos');
    
    // Descargar archivo
    XLSX.writeFile(wb, `plantilla_${templateName}.xlsx`);
    toast.success('Plantilla Excel descargada');
  };

  const handleClose = () => {
    setShowDialog(false);
    setSelectedFile(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <Button 
        variant="outline" 
        onClick={() => setShowDialog(true)}
        data-testid={`carga-masiva-${templateName}-button`}
      >
        <Upload size={18} className="mr-2" />
        Carga Masiva
      </Button>

      <Dialog open={showDialog} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="text-green-600" size={24} />
              Carga Masiva de {entityName}
            </DialogTitle>
            <DialogDescription>
              Sube un archivo Excel con los datos a importar
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Instrucciones */}
            <div className="bg-blue-50 p-4 rounded-lg text-sm">
              <p className="font-semibold text-blue-800 mb-2">Instrucciones:</p>
              <ol className="list-decimal list-inside space-y-1 text-blue-700">
                <li>Descarga la plantilla Excel</li>
                <li>Completa los datos (puedes eliminar la fila de ejemplo)</li>
                <li>Guarda el archivo</li>
                <li>Sube el archivo aquí</li>
              </ol>
              <p className="mt-2 text-blue-600">
                <strong>Columnas:</strong> {templateColumns.join(', ')}
              </p>
            </div>

            {/* Botón descargar plantilla */}
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={downloadTemplate}
              data-testid="download-template-button"
            >
              <Download size={18} className="mr-2" />
              Descargar Plantilla Excel
            </Button>

            {/* Input de archivo */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="excel-upload"
                data-testid="excel-file-input"
              />
              <label htmlFor="excel-upload" className="cursor-pointer">
                <FileSpreadsheet size={40} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  {selectedFile ? (
                    <span className="text-primary font-medium">{selectedFile.name}</span>
                  ) : (
                    'Haz clic para seleccionar archivo Excel'
                  )}
                </p>
              </label>
            </div>

            {/* Resultados */}
            {result && (
              <div className={`p-4 rounded-lg ${result.created > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {result.created > 0 ? (
                    <CheckCircle className="text-green-600" size={20} />
                  ) : (
                    <AlertCircle className="text-red-600" size={20} />
                  )}
                  <span className={`font-semibold ${result.created > 0 ? 'text-green-800' : 'text-red-800'}`}>
                    {result.created} registros creados
                  </span>
                </div>
                
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-red-700 mb-1">
                      Errores ({result.errors.length}):
                    </p>
                    <div className="max-h-32 overflow-y-auto text-xs text-red-600 space-y-1">
                      {result.errors.map((error, idx) => (
                        <p key={idx}>• {error}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Cerrar
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!selectedFile || uploading}
              data-testid="upload-excel-button"
            >
              {uploading ? 'Procesando...' : 'Subir y Procesar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
